<template>
  <div
    class="target-adset-tree"
    v-loading="loading"
  >
    <div class="target-adset-tree__bar">
      <el-checkbox
        :indeterminate="indeterminate"
        :value="allChecked"
        :disabled="!adsetIds.length"
        @change="toggleAll"
        >全选</el-checkbox
      >
      <el-input
        v-model="localKeyword"
        size="mini"
        clearable
        placeholder="搜索广告组，回车查询"
        @input="filterLocal"
        @clear="searchRemote"
        @keyup.enter.native="searchRemote"
      ></el-input>
    </div>
    <p
      v-if="reachLimit"
      class="target-adset-tree__limit"
    >
      已达 200 条上限，可能还有未显示的广告组。请减少系列或按名称搜索。全选只含已加载的广告组。
    </p>
    <el-tree
      v-if="nodes.length"
      ref="tree"
      :data="nodes"
      node-key="id"
      show-checkbox
      default-expand-all
      :indent="18"
      :props="treeProps"
      :filter-node-method="filterNode"
      @check="onCheck"
    >
      <span
        slot-scope="{ data }"
        class="target-adset-tree__node"
        :class="'is-' + data.level"
      >
        <span
          class="name"
          :title="nodeTitle(data)"
          >{{ data.name }}</span
        >
        <span
          v-if="data.status || data.extra || data.code"
          class="side"
        >
          <span
            v-if="data.status"
            class="tag"
            :class="statusTone(data.status)"
            >{{ data.status }}</span
          >
          <span
            v-if="data.extra"
            class="extra"
            :title="data.extra"
            >{{ data.extra }}</span
          >
          <span
            v-if="data.code"
            class="code"
            :title="data.code"
            >{{ data.code }}</span
          >
        </span>
      </span>
    </el-tree>
    <div
      v-else-if="!loading"
      class="target-adset-tree__empty"
    >
      {{ emptyText || '暂无广告组' }}
    </div>
  </div>
</template>
<script>
export default {
  name: 'TargetAdsetTree',
  props: {
    nodes: {
      type: Array,
      default: () => [],
    },
    value: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    reachLimit: {
      type: Boolean,
      default: false,
    },
    emptyText: {
      type: String,
      default: '',
    },
    remoteKeyword: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      localKeyword: '',
      treeProps: {
        label: 'name',
        children: 'children',
        disabled: 'disabled',
      },
    };
  },
  computed: {
    adsetIds() {
      const ids = [];
      (this.nodes || []).forEach((campaign) => {
        (campaign.children || []).forEach((adset) => {
          if (adset.level === 'adset' && adset.id) ids.push(adset.id);
        });
      });
      return ids;
    },
    allChecked() {
      return this.adsetIds.length > 0 && this.adsetIds.every((id) => (this.value || []).includes(id));
    },
    indeterminate() {
      const selected = this.adsetIds.filter((id) => (this.value || []).includes(id)).length;
      return selected > 0 && selected < this.adsetIds.length;
    },
  },
  watch: {
    value() {
      this.syncChecked();
    },
    nodes() {
      this.localKeyword = this.remoteKeyword || '';
      this.syncChecked();
      this.$nextTick(() => {
        if (this.$refs.tree) this.$refs.tree.filter('');
      });
    },
  },
  mounted() {
    this.syncChecked();
  },
  methods: {
    syncChecked() {
      this.$nextTick(() => {
        if (this.$refs.tree) {
          this.$refs.tree.setCheckedKeys(this.value || []);
        }
      });
    },
    onCheck() {
      const nodes = this.$refs.tree.getCheckedNodes(false) || [];
      const ids = nodes.filter((node) => node.level === 'adset').map((node) => node.id);
      this.$emit('input', ids);
    },
    toggleAll(checked) {
      this.$emit('input', checked ? this.adsetIds.slice() : []);
    },
    filterLocal(value) {
      if (this.$refs.tree) this.$refs.tree.filter(value);
    },
    filterNode(value, data, node) {
      if (!value) return true;
      if (this.matchText(data, value)) return true;
      if (data.level === 'adset' && node && node.parent && node.parent.data) {
        return this.matchText(node.parent.data, value);
      }
      if (data.level === 'campaign') {
        return (data.children || []).some((child) => this.matchText(child, value));
      }
      return false;
    },
    nodeMeta(data) {
      if (!data) return '';
      return [data.status, data.extra, data.code].filter(Boolean).join(' ');
    },
    statusTone(status) {
      if (status === '投放中') return 'is-on';
      if (status === '已拒登' || status === '错误') return 'is-bad';
      if (status === '待审核' || status === '审核中' || status === '预先批准' || status === '待账单信息') return 'is-wait';
      return 'is-off';
    },
    nodeTitle(data) {
      const meta = this.nodeMeta(data);
      return [data && data.name, meta].filter(Boolean).join(' ');
    },
    matchText(data, value) {
      const keyword = String(value).trim().toLowerCase();
      return `${(data && data.name) || ''} ${this.nodeMeta(data)}`.toLowerCase().includes(keyword);
    },
    searchRemote() {
      this.$emit('search', (this.localKeyword || '').trim());
    },
  },
};
</script>
<style lang="scss" scoped>
.target-adset-tree__bar {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  .el-checkbox {
    margin-right: 8px;
  }
  .el-input {
    flex: 1;
  }
}
.target-adset-tree__limit {
  margin: 0 0 8px;
  color: #e6a23c;
  font-size: 12px;
  line-height: 1.4;
}
.target-adset-tree__empty {
  color: #999;
  font-size: 12px;
  line-height: 32px;
}
.el-tree {
  max-height: 360px;
  overflow: auto;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  ::v-deep .el-tree-node__content {
    height: auto;
    align-items: flex-start;
    padding-top: 8px;
    padding-bottom: 8px;
  }
  ::v-deep .el-tree-node__expand-icon {
    padding: 3px 4px;
  }
}
.target-adset-tree__node {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  font-size: 13px;
  line-height: 18px;
  color: #303133;
  .name {
    white-space: normal;
    word-break: break-word;
    overflow-wrap: anywhere;
    line-height: 20px;
  }
  .side {
    display: flex;
    align-items: center;
    margin-top: 4px;
  }
  .tag {
    margin-right: 6px;
    padding: 0 6px;
    border-radius: 2px;
    background: #f4f4f5;
    color: #909399;
    font-size: 12px;
    line-height: 18px;
  }
  .tag.is-on {
    background: #f0f9eb;
    color: #67c23a;
  }
  .tag.is-off {
    background: #f4f4f5;
    color: #909399;
  }
  .tag.is-bad {
    background: #fef0f0;
    color: #f56c6c;
  }
  .tag.is-wait {
    background: #fdf6ec;
    color: #e6a23c;
  }
  .extra,
  .code {
    color: #909399;
    font-size: 12px;
    line-height: 18px;
  }
  .code {
    margin-left: 6px;
  }
  &.is-adset .name {
    color: #606266;
  }
}
</style>
